/**
 * @fileoverview Pre-send file chip rendered in the chat input area.
 *
 * Hidden behind the `fileUpload` feature flag. A chip represents a single
 * attached file before the message is sent. It shows a type icon, the
 * (truncated) filename, the formatted size, and a ✕ remove button.
 *
 * Visual states (driven by {@link UploadFileStatus}):
 *   - UPLOADING: a spinner replaces the type icon.
 *   - READY:     normal colors.
 *   - ERROR:     red border + inline error message.
 *
 * This component is purely presentational — all state lives in the parent.
 */

import React from "react";
import { Loader2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { UploadFile, UploadFileStatus } from "./types";
import {
  formatFileSize,
  getFileTypeIcon,
  truncateFilename,
} from "./fileUploadUtils";

interface FileChipProps {
  /** The file to render. */
  file: UploadFile;
  /** Called with the file id when the user clicks the ✕ remove button. */
  onRemove: (id: string) => void;
}

/**
 * Renders a single pre-send file chip.
 */
export const FileChip = ({ file, onRemove }: FileChipProps) => {
  const isUploading = file.status === UploadFileStatus.UPLOADING;
  const isError = file.status === UploadFileStatus.ERROR;
  const TypeIcon = getFileTypeIcon(file.type);

  return (
    <div
      data-testid="file-chip"
      data-status={file.status}
      className={cn(
        "inline-flex max-w-xs items-center gap-2 rounded-md border px-2 py-1 text-xs",
        isError
          ? "border-destructive bg-destructive/5 text-destructive"
          : "border-border bg-muted/40 text-foreground",
      )}
    >
      {/* Leading icon: spinner while uploading, else the file-type icon. */}
      {isUploading ? (
        <Loader2
          className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-muted-foreground"
          data-testid="file-chip-spinner"
        />
      ) : (
        <TypeIcon
          className={cn(
            "h-3.5 w-3.5 flex-shrink-0",
            isError ? "text-destructive" : "text-muted-foreground",
          )}
        />
      )}

      {/* Filename + size (and error message when present). */}
      <span className="flex min-w-0 flex-col">
        <span className="flex items-center gap-1.5">
          <span className="truncate font-medium" title={file.name}>
            {truncateFilename(file.name)}
          </span>
          <span
            className={cn(
              "flex-shrink-0",
              isError ? "text-destructive/80" : "text-muted-foreground",
            )}
          >
            {formatFileSize(file.size)}
          </span>
        </span>
        {isError && file.errorMessage && (
          <span className="truncate text-[11px]" title={file.errorMessage}>
            {file.errorMessage}
          </span>
        )}
      </span>

      {/* Remove (✕) button. */}
      <button
        type="button"
        onClick={() => onRemove(file.id)}
        aria-label={`Remove ${file.name}`}
        className={cn(
          "flex-shrink-0 rounded-sm p-0.5 transition-colors",
          isError
            ? "text-destructive hover:bg-destructive/10"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default FileChip;
